import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from '../../../context/FormContext';
import { useReadOnly } from '../../../hooks/useReadOnly';
import { useCollectJs } from '../../../hooks/useCollectJs';
import { MdLockOutline, MdArrowBack, MdArrowForward } from 'react-icons/md';
import { SignupLayout } from '../../ui/SignupLayout';

import { Spinner } from 'reactstrap';

const ACCOUNT_TYPES = [
    { value: 'checking', label: 'Checking' },
    { value: 'savings', label: 'Savings' },
];

export function BankDetails({ history }) {
    const { formData, setStep, setFields } = useFormContext();
    const { isReadOnly, readOnlyProps } = useReadOnly();
    const collectJsLoaded = useCollectJs();

    // NON-SENSITIVE fields only.
    // Routing, account number, AND the account-holder name are all Collect.js hosted fields (checkaba / checkaccount / checkname),
    const [payment, setPayment] = useState({
        BankAccountAccountType: '',
    });

    const [errors, setErrors] = useState({});
    const [tokenizing, setTokenizing] = useState(false);
    // Collect.js reports its hosted fields are mounted.
    const [fieldsReady, setFieldsReady] = useState(false);
    // Per-field validity reported by Collect.js.
    const [fieldValid, setFieldValid] = useState({
        checkname: false,
        checkaba: false,
        checkaccount: false,
    });
    const configuredRef = useRef(false);
    const paymentRef = useRef(payment);
    paymentRef.current = payment;

    const setPaymentField = (field, value) =>
        setPayment((prev) => ({ ...prev, [field]: value }));

    const paymentError = history.location?.state?.paymentError || false;
    const paymentErrorMessage = history.location?.state?.paymentErrorMessage || '';

    useEffect(() => {
        setStep('paymentBank');
    }, []);

    // Restore NON-SENSITIVE fields if the user navigated back from review.
    useEffect(() => {
        const returned = history.location?.state?.billingInfo;
        if (returned) setPayment(returned);
    }, []);

    // Configure Collect.js hosted ACH fields once the script is loaded.
    useEffect(() => {
        if (!collectJsLoaded || configuredRef.current || isReadOnly) return;
        if (!window.CollectJS) return;
        configuredRef.current = true;

        window.CollectJS.configure({
            variant: 'inline',
            fields: {
                // All three ACH fields must be registered or tokenization stalls.
                checkname: { selector: '#cj-checkname', placeholder: 'Name on account' },
                checkaba: { selector: '#cj-checkaba', placeholder: '123456789' },
                checkaccount: { selector: '#cj-checkaccount', placeholder: '000123456789' },
            },
            fieldsAvailableCallback: () => {
                setFieldsReady(true);
            },
            validationCallback: (field, status, message) => {
                setFieldValid((prev) => ({ ...prev, [field]: status }));
                setErrors((prev) => {
                    const next = { ...prev };
                    if (status) delete next[field];
                    else next[field] = message;
                    return next;
                });
            },
            callback: (response) => {
                setTokenizing(false);
                if (!response?.token) {
                    setErrors((prev) => ({ ...prev, _form: 'Could not read your account details. Please re-enter and try again.' }));
                    return;
                }

                const idempotencyKey =
                    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
                    `${formData.QuoteId}-${Date.now()}`;
                setFields({ PaymentIdempotencyKey: idempotencyKey });

                history.push('/signup/payment/review', {
                    paymentToken: response.token,
                    paymentType: 'ach',
                    billingInfo: paymentRef.current,
                    bankMeta: {
                        last4: response.check?.account ? response.check.account.slice(-4) : '',
                        name: response.check?.name || '',
                    },
                });
            },
        });
    }, [collectJsLoaded, isReadOnly]);

    // Validate the NON-SENSITIVE fields.
    const validate = () => {
        const e = {};
        if (!payment.BankAccountAccountType)
            e.BankAccountAccountType = 'Please select an account type.';
        return e;
    };

    const handleReview = () => {
        if (isReadOnly) {
            history.push('/signup/payment/review');
            return; // skip validation, tokenization, everything
        }
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setErrors({});

        if (!fieldsReady || !window.CollectJS) {
            setErrors({ _form: 'Secure account fields are still loading. Please try again in a moment.' });
            return;
        }

        setTokenizing(true);
        window.CollectJS.startPaymentRequest();
    };

    const handleBack = () => history.push('/signup/payment');

    // All hosted ACH fields must be valid before Review can be clicked.
    const allBankFieldsValid = fieldValid.checkname && fieldValid.checkaba && fieldValid.checkaccount;

    return (
        <SignupLayout showProgress={false}>

            {paymentError && (
                <div className="payment-error-banner">
                    <MdLockOutline size={20} className="payment-error-banner__icon" />
                    <div>
                        <strong>Payment unsuccessful</strong>
                        <p>{paymentErrorMessage || "We couldn't process your payment. Review your details below and resubmit."}</p>
                    </div>
                </div>
            )}

            <h1 className="text-h1">Enter bank account details.</h1>
            <p className="text-default" style={{marginBottom: `var(--space-lg)`}}>
                Connect your bank account for secure ACH payment processing.
            </p>

            {errors._form && (
                <span className="error-text field-error" role="alert">{errors._form}</span>
            )}

            {!fieldsReady && (
                <div className="dp-loading" aria-label="Loading payment fields…">
                    <Spinner style={{color: `var(--brand-primary)`}}/>
                </div>
            )}

            <div
                style={{
                    opacity: fieldsReady ? 1 : 0,
                    transition: 'opacity 250ms ease',
                    // pointerEvents: fieldsReady ? 'auto' : 'none'
                }}
            >
                {/* Account holder name - Collect.js hosted iframe */}
                <div className="form-group">
                    <p className="text-label"><strong>Account holder name</strong></p>
                    <div id="cj-checkname" className="collectjs-field" />
                    {errors.checkname && (
                        <span className="error-text field-error">{errors.checkname}</span>
                    )}
                </div>

                <div className="form-group">
                    <p className="text-label"><strong>Account type</strong></p>
                    <select
                        value={payment.BankAccountAccountType}
                        onChange={(e) => setPaymentField('BankAccountAccountType', e.target.value)}
                        {...readOnlyProps}
                    >
                        <option value="">Select</option>
                        {ACCOUNT_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                    {errors.BankAccountAccountType && (
                        <span className="error-text field-error">{errors.BankAccountAccountType}</span>
                    )}
                </div>

                {/* Routing number - Collect.js hosted iframe */}
                <div className="form-group">
                    <p className="text-label"><strong>Routing number</strong></p>
                    <div id="cj-checkaba" className="collectjs-field" />
                    {errors.checkaba && (
                        <span className="error-text field-error">{errors.checkaba}</span>
                    )}
                </div>

                {/* Account number - Collect.js hosted iframe */}
                <div className="form-group">
                    <p className="text-label"><strong>Account number</strong></p>
                    <div id="cj-checkaccount" className="collectjs-field" />
                    {errors.checkaccount && (
                        <span className="error-text field-error">{errors.checkaccount}</span>
                    )}
                </div>
            </div>

            <div className="security-banner">
                <MdLockOutline size={20} className="security-banner__icon" />
                <div className="security-banner__text">
                    <strong>Pay with confidence.</strong>
                    <p>
                        We use bank-level encryption and never share your account details.
                        ACH payments typically take 1-2 business days to process.
                    </p>
                </div>
            </div>

            <div className="signup-nav">
                <button className="btn signup-cta btn--inverse-cta" onClick={handleBack} disabled={tokenizing}>
                    <MdArrowBack /> Back
                </button>
                <button className="btn signup-cta" onClick={handleReview} disabled={tokenizing || (!isReadOnly && (!fieldsReady || !allBankFieldsValid))}>
                    {tokenizing ? 'Securing…' : <>Review <MdArrowForward /></>}
                </button>
            </div>
        </SignupLayout>
    );
}